import React, { useState, useEffect, useMemo } from 'react'
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import { parseJsonField } from '../../utils/parseJsonField'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Edit2,
  Trash2, 
  Eye, 
  Star,
  X,
  MapPin,
  Bed,
  Users,
  Save,
  ArrowLeft,
  BookOpen,
  Calendar,
  Settings,
  ChevronLeft,
  ChevronRight,
  DollarSign
} from 'lucide-react'
import ImageUpload from './ImageUpload'

interface Hotel {
  id: string
  name: string
  city: string
  country: string
  latitude: number | null
  longitude: number | null
  star_rating: number
  base_price: number
  total_rooms: number
  description: string
  amenities: string[]
  images: Array<{
    id: string
    url: string
    thumbnail_url?: string
    label: string
    description?: string
    filename?: string
    created_at?: string
  }>
  status: 'active' | 'inactive'
  created_at: string
}

interface RoomType {
  id: string
  accommodation_id: string
  name: string
  description: string
  max_occupancy: number
  bed_configuration: string
  room_size: number
  amenities: string[]
  images: string[]
  base_price: number
  currency: string
  total_rooms: number
  is_active: boolean
  created_at: string
  updated_at: string
}

// Currencies list for room types
const currencies = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' }
]

interface ImageData {
  id: string
  url: string
  file?: File
  label: string
  description?: string
  isNew?: boolean
}

interface HotelManagementProps {
  tenantId: string
}

const HotelManagement: React.FC<HotelManagementProps> = ({ tenantId }) => {
  return (
    <Routes>
      <Route path="/" element={<HotelList tenantId={tenantId} />} />
      <Route path="/new" element={<HotelForm tenantId={tenantId} />} />
      <Route path="/edit/:id" element={<HotelForm tenantId={tenantId} />} />
      <Route path="/view/:id" element={<HotelView tenantId={tenantId} />} />
    </Routes>
  )
}

// Hotel List Component
const HotelList: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [hotels, setHotels] = useState<Hotel[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')

  useEffect(() => {
    fetchHotels()
  }, [tenantId])

  const fetchHotels = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/accommodations?tenant_id=${tenantId}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const data = await response.json()
      if (data.success) {
        const processedHotels = data.accommodations.map((hotel: any) => ({
          ...hotel,
          latitude: hotel.latitude ?? null,
          longitude: hotel.longitude ?? null,
          images: parseJsonField(hotel.images, [])
        }))
        setHotels(processedHotels)
      } else {
        throw new Error(`API Error: ${data.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Failed to fetch hotels:', error)
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        alert('⚠️ Backend server is not running!\n\nPlease start the backend server:\ncd backend && python3 -m uvicorn app.main:app --reload --port 8000')
      }
      // Don't show alert for hotel list errors - just log them
    } finally {
      setLoading(false)
    }
  }

  const deleteHotel = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) return
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/accommodations/${id}?tenant_id=${tenantId}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        setHotels(hotels.filter(hotel => hotel.id !== id))
      }
    } catch (error) {
      console.error('Failed to delete hotel:', error)
    }
  }

  // Helper function to construct image URLs consistently
  const getImageUrl = (imageUrl: string) => {
    if (!imageUrl) return ''
    // Handle URLs that already have the correct format
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl
    }
    // Handle absolute paths starting with /uploads/
    if (imageUrl.startsWith('/uploads/')) {
      return `${API_BASE_URL}${imageUrl}`
    }
    // Handle relative paths - add full prefix
    const path = imageUrl.startsWith('uploads/') ? imageUrl : `uploads/images/${imageUrl}`
    return `${API_BASE_URL}/${path}`
  }

  const filteredHotels = hotels.filter(hotel => {
    const matchesSearch = hotel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hotel.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hotel.country.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterStatus === 'all' || hotel.status === filterStatus
    return matchesSearch && matchesFilter
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hotel Management</h1>
          <p className="text-gray-600">Manage your accommodation inventory</p>
        </div>
        <Link
          to="/dashboard/hotels/new"
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-150"
        >
          <Plus className="w-4 h-4" />
          Add Hotel
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search hotels by name, city, or country..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Hotels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredHotels.map((hotel) => (
          <div key={hotel.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Hotel Image */}
            <div className="h-48 bg-gray-200 relative">
              {hotel.images && Array.isArray(hotel.images) && hotel.images.length > 0 ? (
                <img
                  src={getImageUrl(
                    typeof hotel.images[0] === 'string'
                      ? hotel.images[0]
                      : (hotel.images[0].thumbnail_url || hotel.images[0].url)
                  )}
                  alt={hotel.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Bed className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <div className="absolute top-2 right-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  hotel.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {hotel.status}
                </span>
              </div>
            </div>

            {/* Hotel Info */}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{hotel.name}</h3>
                  <div className="flex items-center text-gray-600 text-sm mt-1">
                    <MapPin className="w-4 h-4 mr-1" />
                    {hotel.city}, {hotel.country}
                  </div>
                </div>
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < hotel.star_rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-3 text-sm text-gray-600">
                <div className="flex items-center">
                  <Bed className="w-4 h-4 mr-1" />
                  {hotel.total_rooms} rooms
                </div>
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 mr-1" />
                  ${hotel.base_price}/night
                </div>
              </div>

              <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                {hotel.description}
              </p>

              {/* Management Features Hint */}
              <div className="mt-3 p-2 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 font-medium">
                  🏨 Click "Manage" to configure room types, availability & pricing
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
                <Link
                  to={`/dashboard/hotels/view/${hotel.id}`}
                  className="flex items-center gap-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 px-3 py-2 rounded-lg transition-colors"
                  title="View hotel details, manage room types, availability & pricing"
                >
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium">Manage</span>
                </Link>
                <Link
                  to={`/dashboard/hotels/edit/${hotel.id}`}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                  title="Edit hotel details"
                >
                  <Edit className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => deleteHotel(hotel.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                  title="Delete hotel"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredHotels.length === 0 && (
        <div className="text-center py-12">
          <Bed className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hotels found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.' 
              : 'Get started by adding your first hotel.'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <div className="mt-6">
              <Link
                to="/dashboard/hotels/new"
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Hotel
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Hotel Form Component (for both create and edit)
const HotelForm: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const isEdit = !!id
  const [hotel, setHotel] = useState({
    name: '',
    city: '',
    country: '',
    latitude: null as number | null,
    longitude: null as number | null,
    star_rating: 3,
    base_price: 0,
    total_rooms: 0,
    description: '',
    amenities: [] as string[],
    images: [],
    currency: 'USD',
    status: 'active' as 'active' | 'inactive'
  })
  
  const [imageData, setImageData] = useState<ImageData[]>([])

  useEffect(() => {
    if (id) {
      fetchHotel()
    }
  }, [id, tenantId])

  const fetchHotel = async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await authFetch(`${API_BASE_URL}/api/admin/accommodations/${id}?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        const acc = data.accommodation
        setHotel({
          name: acc.name,
          city: acc.city,
          country: acc.country,
          latitude: acc.latitude ?? null,
          longitude: acc.longitude ?? null,
          star_rating: acc.star_rating,
          base_price: acc.base_price,
          total_rooms: acc.total_rooms,
          description: acc.description || '',
          amenities: acc.amenities || [],
          images: parseJsonField(acc.images, []),
          currency: acc.currency || 'USD',
          status: acc.status
        })
        
        // Load existing images with metadata
        if (acc.images && acc.images.length > 0) {
          const existingImages: ImageData[] = acc.images.map((img: any) => ({
            id: img.id,
            url: img.url,
            label: img.label,
            description: img.description,
            isNew: false
          }))
          setImageData(existingImages)
        }
      }
    } catch (error) {
      console.error('Failed to fetch hotel:', error)
    } finally {
      setLoading(false)
    }
  }

  const commonAmenities = [
    'WiFi', 'Pool', 'Gym', 'Spa', 'Restaurant', 'Bar', 'Room Service',
    'Concierge', 'Valet Parking', 'Business Center', 'Conference Rooms',
    'Pet Friendly', 'Airport Shuttle', 'Laundry Service'
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    // Basic validation
    if (!hotel.name.trim()) {
      alert('Hotel name is required')
      setLoading(false)
      return
    }
    if (!hotel.city.trim()) {
      alert('City is required')
      setLoading(false)
      return
    }
    if (!hotel.country.trim()) {
      alert('Country is required')
      setLoading(false)
      return
    }
    if (hotel.star_rating < 1 || hotel.star_rating > 5) {
      alert('Star rating must be between 1 and 5')
      setLoading(false)
      return
    }

    try {
      // Prepare form data for file uploads
      const formData = new FormData()
      
      // Add hotel data (exclude images array as it's handled separately)
      Object.entries(hotel).forEach(([key, value]) => {
        if (key === 'images') {
          // Skip images array - handled separately as file uploads
          return
        } else if (key === 'amenities') {
          formData.append(key, JSON.stringify(value))
        } else if (value === null || value === undefined) {
          // Skip null fields (e.g., latitude/longitude when not set)
          return
        } else {
          formData.append(key, value.toString())
        }
      })
      
      // Add image data
      const imageMetadata: any[] = []
      imageData.forEach((img, index) => {
        if (img.file) {
          // New image file
          formData.append(`images`, img.file)
          imageMetadata.push({
            id: img.id,
            label: img.label,
            description: img.description,
            isNew: true,
            index
          })
        } else {
          // Existing image
          imageMetadata.push({
            id: img.id,
            url: img.url,
            label: img.label,
            description: img.description,
            isNew: false
          })
        }
      })
      
      formData.append('image_metadata', JSON.stringify(imageMetadata))

      const url = isEdit
        ? `${API_BASE_URL}/api/admin/accommodations/${id}?tenant_id=${tenantId}`
        : `${API_BASE_URL}/api/admin/accommodations?tenant_id=${tenantId}`

      const response = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        body: formData
      })

      if (response.ok) {
        navigate('/dashboard/hotels')
      } else {
        const errorText = await response.text()
        console.error('Failed to save hotel:', response.status, response.statusText, errorText)
        alert(`Failed to save hotel: ${response.status} ${response.statusText}\n\nError: ${errorText}`)
      }
    } catch (error) {
      console.error('Error saving hotel:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleAmenity = (amenity: string) => {
    setHotel(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }))
  }

  const deleteImageFromBackend = async (imageId: string): Promise<boolean> => {
    if (!id) return false
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/accommodations/${id}/images/${imageId}?tenant_id=${tenantId}`, {
        method: 'DELETE'
      })
      return response.ok
    } catch (error) {
      console.error('Error deleting image from backend:', error)
      return false
    }
  }

  // Helper function to construct image URLs consistently
  const getImageUrl = (imageUrl: string) => {
    if (!imageUrl) return ''
    // Handle URLs that already have the correct format
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl
    }
    // Handle absolute paths starting with /uploads/
    if (imageUrl.startsWith('/uploads/')) {
      return `${API_BASE_URL}${imageUrl}`
    }
    // Handle relative paths - add full prefix
    const path = imageUrl.startsWith('uploads/') ? imageUrl : `uploads/images/${imageUrl}`
    return `${API_BASE_URL}/${path}`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/hotels')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Hotel' : 'Add New Hotel'}
          </h1>
          <p className="text-gray-600">
            {isEdit ? 'Update accommodation details' : 'Create a new accommodation listing'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hotel Name *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.name}
                onChange={(e) => setHotel(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., The Grand Hotel"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Star Rating *
              </label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.star_rating}
                onChange={(e) => setHotel(prev => ({ ...prev, star_rating: parseInt(e.target.value) }))}
              >
                {[1, 2, 3, 4, 5].map(rating => (
                  <option key={rating} value={rating}>{rating} Star</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.city}
                onChange={(e) => setHotel(prev => ({ ...prev, city: e.target.value }))}
                placeholder="e.g., London"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.country}
                onChange={(e) => setHotel(prev => ({ ...prev, country: e.target.value }))}
                placeholder="e.g., United Kingdom"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="number"
                step="0.000001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.latitude ?? ''}
                onChange={(e) => setHotel(prev => ({ ...prev, latitude: e.target.value ? parseFloat(e.target.value) : null }))}
                placeholder="e.g., -1.950000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="number"
                step="0.000001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.longitude ?? ''}
                onChange={(e) => setHotel(prev => ({ ...prev, longitude: e.target.value ? parseFloat(e.target.value) : null }))}
                placeholder="e.g., 34.160000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Price per Night *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.base_price}
                onChange={(e) => setHotel(prev => ({ ...prev, base_price: parseFloat(e.target.value) }))}
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Rooms *
              </label>
              <input
                type="number"
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={hotel.total_rooms}
                onChange={(e) => setHotel(prev => ({ ...prev, total_rooms: parseInt(e.target.value) }))}
                placeholder="0"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={hotel.description}
              onChange={(e) => setHotel(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe your hotel, its unique features, and what makes it special..."
            />
          </div>

          {/* Amenities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Amenities
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {commonAmenities.map(amenity => (
                <label key={amenity} className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    checked={hotel.amenities.includes(amenity)}
                    onChange={() => toggleAmenity(amenity)}
                  />
                  <span className="ml-2 text-sm text-gray-700">{amenity}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={hotel.status}
              onChange={(e) => setHotel(prev => ({ ...prev, status: e.target.value as any }))}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Images */}
          <div className="col-span-full">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Hotel Images
            </label>
            <ImageUpload
              images={imageData}
              onImagesChange={setImageData}
              maxImages={10}
              accommodationId={id}
              tenantId={tenantId}
              onImageDelete={deleteImageFromBackend}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/hotels')}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isEdit ? 'Update Hotel' : 'Save Hotel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Hotel View Component
const HotelView: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [hotel, setHotel] = useState<Hotel | null>(null)
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([])
  const [selectedImage, setSelectedImage] = useState<any>(null)
  const [showImageModal, setShowImageModal] = useState(false)
  const [editingRoomType, setEditingRoomType] = useState<any>(null)
  const [showRoomTypeModal, setShowRoomTypeModal] = useState(false)
  const [showAddRoomTypeModal, setShowAddRoomTypeModal] = useState(false)
  const [newRoomType, setNewRoomType] = useState({
    name: '',
    description: '',
    max_occupancy: 2,
    bed_configuration: '',
    room_size: 0,
    amenities: [] as string[],
    base_price: 0,
    currency: 'USD',
    total_rooms: 1
  })
  const [roomTypeImages, setRoomTypeImages] = useState<ImageData[]>([])
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false)
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null)
  const [availabilityData, setAvailabilityData] = useState<any[]>([])
  const [loadingAvailability, setLoadingAvailability] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  
  // Pricing management states
  const [showPricingModal, setShowPricingModal] = useState(false)
  const [pricingRules, setPricingRules] = useState<any[]>([])
  const [loadingPricing, setLoadingPricing] = useState(false)
  const [showAddPricingRule, setShowAddPricingRule] = useState(false)
  const [newPricingRule, setNewPricingRule] = useState({
    rule_name: '',
    start_date: '',
    end_date: '',
    price: 0,
    rule_type: 'seasonal',
    priority: 1,
    days_of_week: [] as number[]
  })

  useEffect(() => {
    if (id) {
      fetchHotel()
      fetchRoomTypes()
    }
  }, [id, tenantId])

  const fetchRoomTypes = async () => {
    if (!id) return

    try {
      const response = await authFetch(`${API_BASE_URL}/api/accommodations/${id}/room-types?tenant_id=${tenantId}`)
      const data = await response.json()

      if (data.success) {
        setRoomTypes(data.room_types || [])
      } else {
        console.error('Room types fetch failed:', data)
      }
    } catch (error) {
      console.error('Failed to fetch room types:', error)
    }
  }

  const fetchHotel = async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await authFetch(`${API_BASE_URL}/api/admin/accommodations/${id}?tenant_id=${tenantId}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      if (data.success) {
        const acc = data.accommodation
        setHotel({
          id: acc.id,
          name: acc.name,
          city: acc.city,
          country: acc.country,
          latitude: acc.latitude ?? null,
          longitude: acc.longitude ?? null,
          star_rating: acc.star_rating,
          base_price: acc.base_price,
          total_rooms: acc.total_rooms,
          description: acc.description || '',
          amenities: acc.amenities || [],
          images: parseJsonField(acc.images, []),
          status: acc.status,
          created_at: acc.created_at
        })
      } else {
        throw new Error(`API Error: ${data.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Failed to fetch hotel:', error)
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        alert('⚠️ Backend server is not running!\n\nPlease start the backend server:\ncd backend && python3 -m uvicorn app.main:app --reload --port 8000')
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        alert(`Failed to load hotel details: ${errorMessage}`)
      }
    } finally {
      setLoading(false)
    }
  }

  const openImageModal = (image: any) => {
    setSelectedImage(image)
    setShowImageModal(true)
  }

  const closeImageModal = () => {
    setSelectedImage(null)
    setShowImageModal(false)
  }

  const deleteImage = async (imageId: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/accommodations/${id}/images/${imageId}?tenant_id=${tenantId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        // Refresh hotel data to update the image list
        fetchHotel()
      } else {
        alert('Failed to delete image')
      }
    } catch (error) {
      console.error('Failed to delete image:', error)
      alert('Failed to delete image')
    }
  }

  // Helper function to construct image URLs consistently
  const getImageUrl = (imageUrl: string) => {
    if (!imageUrl) return ''
    // Handle URLs that already have the correct format
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl
    }
    // Handle absolute paths starting with /uploads/
    if (imageUrl.startsWith('/uploads/')) {
      return `${API_BASE_URL}${imageUrl}`
    }
    // Handle relative paths - add full prefix
    const path = imageUrl.startsWith('uploads/') ? imageUrl : `uploads/images/${imageUrl}`
    return `${API_BASE_URL}/${path}`
  }

  const openEditRoomType = (roomType: any) => {
    setEditingRoomType({
      ...roomType,
      amenities: roomType.amenities || []
    })
    setShowRoomTypeModal(true)
  }

  const closeRoomTypeModal = () => {
    setEditingRoomType(null)
    setShowRoomTypeModal(false)
  }

  const updateRoomType = async () => {
    if (!editingRoomType || !id) return
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/room-types/${editingRoomType.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          accommodation_id: id,
          name: editingRoomType.name,
          description: editingRoomType.description,
          max_occupancy: editingRoomType.max_occupancy,
          amenities: editingRoomType.amenities,
          images: editingRoomType.images || [],
          base_price: editingRoomType.base_price,
          currency: editingRoomType.currency || 'USD',
          total_rooms: editingRoomType.total_rooms
        })
      })
      
      if (response.ok) {
        fetchRoomTypes() // Refresh room types
        closeRoomTypeModal()
        alert('Room type updated successfully!')
      } else {
        alert('Failed to update room type')
      }
    } catch (error) {
      console.error('Failed to update room type:', error)
      alert('Failed to update room type')
    }
  }

  const createRoomType = async () => {
    if (!id) return
    
    try {
      // Always use FormData for consistency (whether images exist or not)
      const formData = new FormData()
      
      // Add room type data
      formData.append('accommodation_id', id)
      formData.append('tenant_id', tenantId)
      formData.append('name', newRoomType.name)
      formData.append('description', newRoomType.description)
      formData.append('max_occupancy', newRoomType.max_occupancy.toString())
      formData.append('bed_configuration', newRoomType.bed_configuration)
      formData.append('room_size', newRoomType.room_size.toString())
      formData.append('base_price', newRoomType.base_price.toString())
      formData.append('currency', newRoomType.currency)
      formData.append('total_rooms', newRoomType.total_rooms.toString())
      formData.append('amenities', JSON.stringify(newRoomType.amenities.filter(a => a.trim())))
      
      // Add images (if any)
      const imageMetadata: any[] = []
      roomTypeImages.forEach((img, index) => {
        if (img.file) {
          formData.append('images', img.file)
          imageMetadata.push({
            label: img.label || `Room Image ${index + 1}`,
            description: img.description || ''
          })
        }
      })
      formData.append('image_metadata', JSON.stringify(imageMetadata))

      const response = await authFetch(`${API_BASE_URL}/api/room-types`, {
        method: 'POST',
        body: formData
      })

      if (response.ok) {
        resetForm()

        // Add a small delay to ensure database transaction is committed, then refresh
        setTimeout(() => {
          fetchRoomTypes()
        }, 500)
      } else {
        const error = await response.text()
        console.error('Room type creation failed:', error)
        alert(`Failed to create room type: ${error}`)
      }
    } catch (error) {
      console.error('Network error creating room type:', error)
      alert(`Network error creating room type: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  const resetForm = () => {
    setNewRoomType({
      name: '',
      description: '',
      max_occupancy: 2,
      bed_configuration: '',
      room_size: 0,
      amenities: [],
      base_price: 0,
      currency: 'USD',
      total_rooms: 1
    })
    setRoomTypeImages([])
    setShowAddRoomTypeModal(false)
    // Don't call fetchRoomTypes() here - it's called with a delay in the success handler
  }

  // Availability management functions
  const fetchAvailabilityForMonth = async (roomType: RoomType, monthDate: Date) => {
    setLoadingAvailability(true)
    
    // Get first and last day of the month
    const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
    const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0)
    
    const startDateStr = firstDay.toISOString().split('T')[0]
    const endDateStr = lastDay.toISOString().split('T')[0]
    
    try {
      const url = `${API_BASE_URL}/api/availability/room_type/${roomType.id}?tenant_id=${tenantId}&start_date=${startDateStr}&end_date=${endDateStr}`

      const response = await authFetch(url, {
        method: 'GET',
      })

      if (response.ok) {
        const data = await response.json()
        setAvailabilityData(data.availability || [])
      } else {
        const errorData = await response.json()
        console.error('Failed to fetch availability data:', errorData)
        setAvailabilityData([])
      }
    } catch (error) {
      console.error('Error fetching availability:', error)
      setAvailabilityData([])
    } finally {
      setLoadingAvailability(false)
    }
  }

  const openAvailabilityModal = async (roomType: RoomType) => {
    setSelectedRoomType(roomType)
    setShowAvailabilityModal(true)
    setCurrentMonth(new Date()) // Reset to current month
    await fetchAvailabilityForMonth(roomType, new Date())
  }

  const navigateMonth = async (direction: 'prev' | 'next') => {
    if (!selectedRoomType) return
    
    const newMonth = new Date(currentMonth)
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1)
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1)
    }
    
    setCurrentMonth(newMonth)
    await fetchAvailabilityForMonth(selectedRoomType, newMonth)
  }

  const initializeRoomTypeAvailability = async (roomType: RoomType) => {
    if (!roomType) return
    
    // Initialize the currently viewed month instead of next 30 days
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)
    
    const payload = {
      room_type_id: roomType.id,
      start_date: firstDay.toISOString().split('T')[0],
      end_date: lastDay.toISOString().split('T')[0],
      daily_slots: roomType.total_rooms
    }
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/availability/initialize/room-type`, {
        method: 'POST',
        body: JSON.stringify(payload)
      })

      const result = await response.json()
      
      if (response.ok) {
        const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        alert(`Room type availability initialized for ${monthName}`)
        await fetchAvailabilityForMonth(roomType, currentMonth) // Refresh current month
      } else {
        alert(`Failed to initialize availability: ${result.detail || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error initializing availability:', error)
      alert(`Error initializing availability: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  const updateAvailability = async (date: string, availableSlots: number) => {
    if (!selectedRoomType) return
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/availability/update`, {
        method: 'PUT',
        body: JSON.stringify({
          resource_type: 'room_type',
          resource_id: selectedRoomType.id,
          date: date,
          available_slots: availableSlots
        })
      })
      
      if (response.ok) {
        await fetchAvailabilityForMonth(selectedRoomType, currentMonth) // Refresh current month
      } else {
        alert('Failed to update availability')
      }
    } catch (error) {
      console.error('Error updating availability:', error)
      alert('Error updating availability')
    }
  }

  // Calendar helper functions
  const generateCalendarDays = (month: Date) => {
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1)
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0)
    const startDate = new Date(firstDay)
    startDate.setDate(firstDay.getDate() - firstDay.getDay()) // Start from Sunday
    
    const days = []
    const currentDate = new Date(startDate)
    
    // Generate 42 days (6 weeks x 7 days) to cover the full calendar grid
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }
    
    return days
  }

  const getAvailabilityForDate = (date: Date) => {
    // Use timezone-safe date formatting to avoid date shifting
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateStr = `${year}-${month}-${day}`
    
    // Check both 'date' and 'available_date' fields for compatibility
    const found = availabilityData.find(item =>
      item.date === dateStr || item.available_date === dateStr
    )
    return found
  }

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth() && date.getFullYear() === currentMonth.getFullYear()
  }

  // Pricing Management Functions
  const openPricingModal = (roomType: RoomType) => {
    setSelectedRoomType(roomType)
    setShowPricingModal(true)
    fetchPricingRules(roomType.id)
  }

  const closePricingModal = () => {
    setShowPricingModal(false)
    setSelectedRoomType(null)
    setPricingRules([])
    setShowAddPricingRule(false)
    setNewPricingRule({
      rule_name: '',
      start_date: '',
      end_date: '',
      price: 0,
      rule_type: 'seasonal',
      priority: 1,
      days_of_week: []
    })
  }

  const fetchPricingRules = async (roomTypeId: string) => {
    setLoadingPricing(true)
    try {
      const response = await authFetch(`${API_BASE_URL}/api/room-types/${roomTypeId}/pricing-rules?tenant_id=${tenantId}`)
      if (response.ok) {
        const data = await response.json()
        setPricingRules(data.pricing_rules || [])
      } else {
        console.error('Failed to fetch pricing rules')
        setPricingRules([])
      }
    } catch (error) {
      console.error('Error fetching pricing rules:', error)
      setPricingRules([])
    } finally {
      setLoadingPricing(false)
    }
  }

  const createPricingRule = async () => {
    if (!selectedRoomType || !newPricingRule.rule_name || !newPricingRule.start_date || !newPricingRule.end_date || newPricingRule.price <= 0) {
      alert('Please fill in all required fields')
      return
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/api/room-types/${selectedRoomType.id}/pricing-rules?tenant_id=${tenantId}`, {
        method: 'POST',
        body: JSON.stringify(newPricingRule)
      })

      if (response.ok) {
        fetchPricingRules(selectedRoomType.id)
        setShowAddPricingRule(false)
        setNewPricingRule({
          rule_name: '',
          start_date: '',
          end_date: '',
          price: 0,
          rule_type: 'seasonal',
          priority: 1,
          days_of_week: []
        })
        alert('Pricing rule created successfully!')
      } else {
        const error = await response.json()
        alert(`Failed to create pricing rule: ${error.detail}`)
      }
    } catch (error) {
      console.error('Error creating pricing rule:', error)
      alert('Failed to create pricing rule')
    }
  }

  const deletePricingRule = async (ruleId: string) => {
    if (!selectedRoomType || !window.confirm('Are you sure you want to delete this pricing rule?')) {
      return
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/api/room-types/${selectedRoomType.id}/pricing-rules/${ruleId}?tenant_id=${tenantId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        fetchPricingRules(selectedRoomType.id)
        alert('Pricing rule deleted successfully!')
      } else {
        const error = await response.json()
        alert(`Failed to delete pricing rule: ${error.detail}`)
      }
    } catch (error) {
      console.error('Error deleting pricing rule:', error)
      alert('Failed to delete pricing rule')
    }
  }

  // Amenity icons mapping
  const getAmenityIcon = (amenity: string) => {
    const lowerAmenity = amenity.toLowerCase()
    if (lowerAmenity.includes('wifi')) return '📶'
    if (lowerAmenity.includes('pool')) return '🏊‍♀️'
    if (lowerAmenity.includes('gym') || lowerAmenity.includes('fitness')) return '🏋️‍♂️'
    if (lowerAmenity.includes('spa')) return '💆‍♀️'
    if (lowerAmenity.includes('restaurant')) return '🍽️'
    if (lowerAmenity.includes('bar')) return '🍷'
    if (lowerAmenity.includes('business') || lowerAmenity.includes('center')) return '💼'
    if (lowerAmenity.includes('conference') || lowerAmenity.includes('rooms')) return '🏛️'
    if (lowerAmenity.includes('laundry')) return '🧺'
    return '✨'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (!hotel) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Hotel not found</h3>
        <p className="text-gray-500">The requested hotel could not be found.</p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/dashboard/hotels')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
          >
            Back to Hotels
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Purple Header Section */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/dashboard/hotels')}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <Link
              to={`/dashboard/hotels/edit/${hotel.id}`}
              className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit Hotel
            </Link>
          </div>

          {/* Hotel Info Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{hotel.name}</h1>
            <p className="text-gray-600 mb-4">{hotel.city}, {hotel.country}</p>
            
            <div className="flex items-center justify-center gap-6 text-sm">
              {/* Star Rating */}
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < hotel.star_rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-gray-700 font-medium">{hotel.star_rating} Star</span>
              </div>
              
              {/* Room Count */}
              <div className="flex items-center gap-1 text-gray-600">
                <Bed className="w-4 h-4" />
                <span>{hotel.total_rooms} Rooms</span>
              </div>
              
              {/* Price */}
              <div className="flex items-center gap-1 text-gray-600">
                <DollarSign className="w-4 h-4" />
                <span>${hotel.base_price}/night</span>
              </div>
              
              {/* Status */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                hotel.status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {hotel.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Description and Amenities */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Description</h2>
              </div>
              <p className="text-gray-600 leading-relaxed text-left">
                {hotel.description || 'No description available.'}
              </p>
            </div>

            {/* Amenities Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Star className="w-4 h-4 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Amenities</h2>
              </div>
              {hotel.amenities.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {hotel.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg min-h-[48px]">
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        <span className="text-base">{getAmenityIcon(amenity)}</span>
                      </div>
                      <span className="text-sm text-gray-700 font-medium flex-1">{amenity}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No amenities listed.</p>
              )}
            </div>

            {/* Images Gallery Card */}
            {hotel.images && Array.isArray(hotel.images) && hotel.images.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Eye className="w-4 h-4 text-purple-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Hotel Gallery</h2>
                  <span className="text-sm text-gray-500">({Array.isArray(hotel.images) ? hotel.images.length : 0} images)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.isArray(hotel.images) && hotel.images.map((image, index) => (
                    <div key={image.id} className="relative group rounded-lg overflow-hidden bg-gray-100">
                      <div className="cursor-pointer" onClick={() => openImageModal(image)}>
                        <img
                          src={getImageUrl(image.thumbnail_url || image.url)}
                          alt={image.label}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <div className="text-white">
                          <p className="font-medium text-sm">{image.label}</p>
                          {image.description && (
                            <p className="text-xs text-white/80 mt-1">{image.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                        {index + 1}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteImage(image.id)
                        }}
                        className="absolute top-2 left-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        title="Delete image"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Room Types Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Bed className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Room Types</h2>
                  <span className="text-sm text-gray-500">({roomTypes.length} types)</span>
                </div>
                <button
                  onClick={() => setShowAddRoomTypeModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Room Type
                </button>
              </div>
              
              {roomTypes.length > 0 ? (
                <div className="space-y-4">
                  {roomTypes.map((roomType) => (
                    <div key={roomType.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{roomType.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{roomType.description}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              {(roomType.currency && currencies.find(c => c.code === roomType.currency)?.symbol) || '$'}{roomType.base_price}
                              {roomType.currency && roomType.currency !== 'USD' && (
                                <span className="ml-1 text-sm text-gray-500">{roomType.currency}</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">per night</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openAvailabilityModal(roomType)}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Manage availability"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openPricingModal(roomType)}
                              className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Manage pricing"
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEditRoomType(roomType)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit room type"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Max {roomType.max_occupancy}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Bed className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{roomType.bed_configuration}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">📐 {roomType.room_size}m²</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">🏠 {roomType.total_rooms} rooms</span>
                        </div>
                      </div>
                      
                      {roomType.amenities.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Amenities:</p>
                          <div className="flex flex-wrap gap-2">
                            {roomType.amenities.slice(0, 6).map((amenity, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                                {getAmenityIcon(amenity)} {amenity}
                              </span>
                            ))}
                            {roomType.amenities.length > 6 && (
                              <span className="text-xs text-gray-500">+{roomType.amenities.length - 6} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic text-center py-8">No room types available for this hotel.</p>
              )}
            </div>
          </div>

          {/* Right Column - Hotel Details and System Info */}
          <div className="space-y-6">
            {/* Hotel Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Hotel Details</h3>
              </div>
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Location</span>
                  <span className="text-gray-900 font-semibold text-right max-w-[60%]">{hotel.city}, {hotel.country}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Star Rating</span>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < hotel.star_rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Total Rooms</span>
                  <span className="text-gray-900 font-semibold">{hotel.total_rooms}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Base Price</span>
                  <span className="text-gray-900 font-semibold">${hotel.base_price}/night</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    hotel.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {hotel.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* System Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-4 h-4 text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">System Info</h3>
              </div>
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Created</span>
                  <span className="text-gray-900 font-semibold text-right">
                    {new Date(hotel.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <span className="text-gray-600 font-medium mb-3 block">Hotel ID</span>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <code className="text-xs text-gray-700 font-mono break-all">{hotel.id}</code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Room Type Modal */}
      {showRoomTypeModal && editingRoomType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit Room Type</h3>
                <button
                  onClick={closeRoomTypeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Name *</label>
                  <input
                    type="text"
                    value={editingRoomType.name}
                    onChange={(e) => setEditingRoomType({...editingRoomType, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Deluxe Room"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Price *</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={editingRoomType.base_price || ''}
                        onChange={(e) => setEditingRoomType({...editingRoomType, base_price: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="w-24">
                      <select
                        value={editingRoomType.currency || 'USD'}
                        onChange={(e) => setEditingRoomType({...editingRoomType, currency: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {currencies.map(currency => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select currency for room pricing
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingRoomType.description || ''}
                  onChange={(e) => setEditingRoomType({...editingRoomType, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Room description..."
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Occupancy</label>
                  <input
                    type="number"
                    value={editingRoomType.max_occupancy || 2}
                    onChange={(e) => setEditingRoomType({...editingRoomType, max_occupancy: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Rooms</label>
                  <input
                    type="number"
                    value={editingRoomType.total_rooms || 1}
                    onChange={(e) => setEditingRoomType({...editingRoomType, total_rooms: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Size (m²)</label>
                  <input
                    type="number"
                    value={editingRoomType.room_size || ''}
                    onChange={(e) => setEditingRoomType({...editingRoomType, room_size: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="25"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bed Configuration</label>
                <select
                  value={editingRoomType.bed_configuration || ''}
                  onChange={(e) => setEditingRoomType({...editingRoomType, bed_configuration: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select bed type</option>
                  <option value="1 King Bed">1 King Bed</option>
                  <option value="1 Queen Bed">1 Queen Bed</option>
                  <option value="2 Single Beds">2 Single Beds</option>
                  <option value="1 Double Bed">1 Double Bed</option>
                  <option value="2 Double Beds">2 Double Beds</option>
                  <option value="1 Sofa Bed">1 Sofa Bed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Room Amenities</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {['WiFi', 'Air Conditioning', 'TV', 'Mini Bar', 'Safe', 'Balcony', 'City View', 'Ocean View', 'Room Service', 'Coffee Maker', 'Hair Dryer', 'Iron', 'Bathtub', 'Shower', 'Work Desk', 'Sofa'].map((amenity) => (
                    <label key={amenity} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingRoomType.amenities?.includes(amenity) || false}
                        onChange={(e) => {
                          const currentAmenities = editingRoomType.amenities || []
                          if (e.target.checked) {
                            setEditingRoomType({
                              ...editingRoomType,
                              amenities: [...currentAmenities, amenity]
                            })
                          } else {
                            setEditingRoomType({
                              ...editingRoomType,
                              amenities: currentAmenities.filter((a: string) => a !== amenity)
                            })
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">{amenity}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeRoomTypeModal}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={updateRoomType}
                disabled={!editingRoomType.name || !editingRoomType.base_price}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update Room Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Room Type Modal */}
      {showAddRoomTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add Room Type</h3>
                <button
                  onClick={() => setShowAddRoomTypeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Name</label>
                <input
                  type="text"
                  value={newRoomType.name}
                  onChange={(e) => setNewRoomType({...newRoomType, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Deluxe Room"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newRoomType.description}
                  onChange={(e) => setNewRoomType({...newRoomType, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Room description..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Occupancy</label>
                  <input
                    type="number"
                    value={newRoomType.max_occupancy}
                    onChange={(e) => setNewRoomType({...newRoomType, max_occupancy: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Rooms</label>
                  <input
                    type="number"
                    value={newRoomType.total_rooms}
                    onChange={(e) => setNewRoomType({...newRoomType, total_rooms: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bed Configuration</label>
                <input
                  type="text"
                  value={newRoomType.bed_configuration}
                  onChange={(e) => setNewRoomType({...newRoomType, bed_configuration: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 1 King Bed, 2 Queen Beds"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Size (m²)</label>
                  <input
                    type="number"
                    value={newRoomType.room_size}
                    onChange={(e) => setNewRoomType({...newRoomType, room_size: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price per Night</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={newRoomType.base_price}
                        onChange={(e) => setNewRoomType({...newRoomType, base_price: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="w-24">
                      <select
                        value={newRoomType.currency}
                        onChange={(e) => setNewRoomType({...newRoomType, currency: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {currencies.map(currency => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select currency for room pricing
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Images</label>
                <div className="space-y-3">
                  {roomTypeImages.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {roomTypeImages.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={image.url}
                            alt={image.label}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => setRoomTypeImages(roomTypeImages.filter((_, i) => i !== index))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ✕
                          </button>
                          <div className="mt-1">
                            <input
                              type="text"
                              value={image.label}
                              onChange={(e) => {
                                const updatedImages = [...roomTypeImages]
                                updatedImages[index].label = e.target.value
                                setRoomTypeImages(updatedImages)
                              }}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              placeholder="Image label"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || [])
                        files.forEach((file) => {
                          const reader = new FileReader()
                          reader.onload = (event) => {
                            const newImage: ImageData = {
                              id: Date.now().toString() + Math.random().toString(),
                              url: event.target?.result as string,
                              file: file,
                              label: file.name.split('.')[0],
                              description: ''
                            }
                            setRoomTypeImages(prev => [...prev, newImage])
                          }
                          reader.readAsDataURL(file)
                        })
                      }}
                      className="hidden"
                      id="room-image-upload"
                    />
                    <label
                      htmlFor="room-image-upload"
                      className="cursor-pointer text-blue-600 hover:text-blue-800"
                    >
                      Click to upload room images
                    </label>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB each</p>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                <div className="space-y-2">
                  {(newRoomType.amenities.length === 0 ? [''] : newRoomType.amenities).map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={amenity}
                        onChange={(e) => {
                          const currentAmenities = newRoomType.amenities.length === 0 ? [''] : newRoomType.amenities
                          const updatedAmenities = [...currentAmenities]
                          updatedAmenities[index] = e.target.value
                          setNewRoomType({...newRoomType, amenities: updatedAmenities})
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter amenity"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const currentAmenities = newRoomType.amenities.length === 0 ? [''] : newRoomType.amenities
                          const updatedAmenities = currentAmenities.filter((_, i) => i !== index)
                          setNewRoomType({...newRoomType, amenities: updatedAmenities})
                        }}
                        className="px-2 py-2 text-red-600 hover:text-red-800"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNewRoomType({...newRoomType, amenities: [...newRoomType.amenities, '']})}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    + Add Amenity
                  </button>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAddRoomTypeModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={createRoomType}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                Create Room Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={closeImageModal}>
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={closeImageModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-xl font-bold z-10"
            >
              ✕
            </button>
            <img
              src={getImageUrl(selectedImage.url)}
              alt={selectedImage.label}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            {(selectedImage.label || selectedImage.description) && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                <div className="text-white">
                  {selectedImage.label && (
                    <h3 className="font-semibold text-lg">{selectedImage.label}</h3>
                  )}
                  {selectedImage.description && (
                    <p className="text-sm text-gray-200 mt-1">{selectedImage.description}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Availability Management Modal */}
      {showAvailabilityModal && selectedRoomType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Availability Management</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedRoomType.name} - {hotel?.name}</p>
                </div>
                <button
                  onClick={() => setShowAvailabilityModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              {loadingAvailability ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Header with Month Navigation */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800">
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </h4>
                        <p className="text-sm text-gray-500">Total rooms: {selectedRoomType.total_rooms}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigateMonth('prev')}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          disabled={loadingAvailability}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigateMonth('next')}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          disabled={loadingAvailability}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => initializeRoomTypeAvailability(selectedRoomType)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={loadingAvailability}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Initialize Month
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {/* Calendar Header */}
                    <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="px-3 py-2 text-center text-sm font-medium text-gray-500">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7">
                      {generateCalendarDays(currentMonth).map((date, index) => {
                        const availability = getAvailabilityForDate(date)
                        const isCurrentMonthDate = isCurrentMonth(date)
                        const isToday = date.toDateString() === new Date().toDateString()
                        
                        return (
                          <div
                            key={index}
                            className={`min-h-[120px] border-r border-b border-gray-200 p-2 ${
                              isCurrentMonthDate ? 'bg-white' : 'bg-gray-50'
                            } ${isToday ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <span className={`text-sm font-medium ${
                                isCurrentMonthDate ? 'text-gray-900' : 'text-gray-400'
                              } ${isToday ? 'text-blue-600 font-bold' : ''}`}>
                                {date.getDate()}
                              </span>
                              {availability && (
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  availability.remaining_slots > 0
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {availability.remaining_slots || 0} left
                                </span>
                              )}
                            </div>

                            {availability && isCurrentMonthDate ? (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-gray-600">Available:</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max={selectedRoomType.total_rooms}
                                    value={availability.available_slots}
                                    onChange={(e) => updateAvailability(availability.date, parseInt(e.target.value) || 0)}
                                    className="w-12 px-1 py-1 border border-gray-300 rounded text-center text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    disabled={loadingAvailability}
                                  />
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-600">
                                  <span>Booked:</span>
                                  <span className="font-medium" data-debug={`Date: ${date.toISOString().split('T')[0]}, Booked: ${availability.booked_slots}, Type: ${typeof availability.booked_slots}`}>{availability.booked_slots || 0}</span>
                                </div>
                                {availability.price && (
                                  <div className="text-xs text-gray-600 text-center">
                                    {(selectedRoomType.currency && currencies.find(c => c.code === selectedRoomType.currency)?.symbol) || '$'}{availability.price}
                                  </div>
                                )}
                              </div>
                            ) : isCurrentMonthDate ? (
                              <div className="text-center">
                                <span className="text-xs text-gray-400">No data</span>
                              </div>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {availabilityData.length === 0 && (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-700 mb-2">No availability data found</h4>
                      <p className="text-sm text-gray-500 mb-4">Initialize availability for this room type to start managing inventory</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pricing Management Modal */}
      {showPricingModal && selectedRoomType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Pricing Management</h3>
                <p className="text-sm text-gray-500">{selectedRoomType.name} - {hotel?.name}</p>
              </div>
              <button
                onClick={closePricingModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="px-6 py-4">
              {loadingPricing ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Header with Add Button */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800">Pricing Rules</h4>
                      <p className="text-sm text-gray-500">Manage date-based pricing for this room type</p>
                    </div>
                    <button
                      onClick={() => setShowAddPricingRule(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Pricing Rule
                    </button>
                  </div>

                  {/* Add Pricing Rule Form */}
                  {showAddPricingRule && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h5 className="text-md font-medium text-gray-900 mb-4">Create New Pricing Rule</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Rule Name</label>
                          <input
                            type="text"
                            value={newPricingRule.rule_name}
                            onChange={(e) => setNewPricingRule({...newPricingRule, rule_name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="e.g., Summer 2025, Holiday Season"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Rule Type</label>
                          <select
                            value={newPricingRule.rule_type}
                            onChange={(e) => setNewPricingRule({...newPricingRule, rule_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="seasonal">Seasonal</option>
                            <option value="holiday">Holiday</option>
                            <option value="weekend">Weekend</option>
                            <option value="special_event">Special Event</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                          <input
                            type="date"
                            value={newPricingRule.start_date}
                            onChange={(e) => setNewPricingRule({...newPricingRule, start_date: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                          <input
                            type="date"
                            value={newPricingRule.end_date}
                            onChange={(e) => setNewPricingRule({...newPricingRule, end_date: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Price (USD)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={newPricingRule.price}
                            onChange={(e) => setNewPricingRule({...newPricingRule, price: parseFloat(e.target.value) || 0})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                          <input
                            type="number"
                            min="1"
                            value={newPricingRule.priority}
                            onChange={(e) => setNewPricingRule({...newPricingRule, priority: parseInt(e.target.value) || 1})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="1"
                          />
                          <p className="text-xs text-gray-500 mt-1">Higher numbers win for overlapping dates</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-4">
                        <button
                          onClick={createPricingRule}
                          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          Create Rule
                        </button>
                        <button
                          onClick={() => setShowAddPricingRule(false)}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Pricing Rules List */}
                  <div className="space-y-3">
                    {pricingRules.length === 0 ? (
                      <div className="text-center py-8">
                        <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-700 mb-2">No pricing rules found</h4>
                        <p className="text-sm text-gray-500 mb-4">Create pricing rules to set different rates for specific date ranges</p>
                      </div>
                    ) : (
                      pricingRules.map((rule) => (
                        <div key={rule.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-md font-medium text-gray-900">{rule.rule_name}</h5>
                              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                                <span>{rule.start_date} to {rule.end_date}</span>
                                <span className="px-2 py-1 bg-gray-100 rounded-full text-xs capitalize">{rule.rule_type}</span>
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">Priority: {rule.priority}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-600">${rule.price}</div>
                              <div className="text-xs text-gray-500">per night</div>
                              <button
                                onClick={() => deletePricingRule(rule.id)}
                                className="mt-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete rule"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HotelManagement